// Multilingual support for workflow (canvas) nodes.
//
// Mirrors the form-field translation model in `formTranslations.js`, but for the
// handful of user-facing text attributes a workflow step carries: its label
// (`name`) and the approval action button labels (`approveLabel`/`rejectLabel`).
//
// Each node keeps its canonical (English/default) values on the existing
// top-level keys it always had. Translations for other languages live in an
// optional `translations` map on the node's `data`, keyed by language code:
//
//   data: {
//     name: "Manager Review",          // default (English) — also the status id
//     approveLabel: "Approve",
//     rejectLabel: "Reject",
//     translations: {
//       japanese: {
//         name: "マネージャー確認",
//         approveLabel: "承認",
//         rejectLabel: "却下",
//       },
//     },
//   }
//
// Only display text is translated; the canonical `name` is still used as the
// workflow status identifier everywhere, so resolution is render-time only. The
// model is backward compatible: nodes saved before this feature simply have no
// `translations` key and resolve to their default values.

import { DEFAULT_LANGUAGE, FORM_LANGUAGES } from "./formTranslations.js";

export { DEFAULT_LANGUAGE, FORM_LANGUAGES };

// Workflows reuse the app-wide language list so canvas dropdowns stay in
// sync with forms, account settings, and the header switcher.
export const WORKFLOW_LANGUAGES = FORM_LANGUAGES;

// The translatable scalar text attributes on a node's `data`.
const NODE_LEAF_KEYS = ["name", "approveLabel", "rejectLabel"];

// Pull the translatable text out of a node data / form values object into a
// compact, language-agnostic shape. Empty strings are kept so an author can
// clear a translation.
export const extractNodeTranslatableValues = (source = {}) => {
    const result = {};
    if (!source || typeof source !== "object") return result;
    for (const key of NODE_LEAF_KEYS) {
        if (typeof source[key] === "string") {
            result[key] = source[key];
        }
    }
    return result;
};

// Merge stored translations for `lng` onto node data for rendering. Missing or
// empty translations fall back to the default value. Returns the original
// reference when nothing changed.
export const resolveNodeTranslation = (data, lng) => {
    if (!data || !lng || lng === DEFAULT_LANGUAGE) return data;
    const overrides = data?.translations?.[lng];
    if (!overrides) return data;

    let result = data;
    for (const key of NODE_LEAF_KEYS) {
        const value = overrides[key];
        if (typeof value === "string" && value !== "") {
            if (result === data) result = { ...data };
            result[key] = value;
        }
    }
    return result;
};

// Write a single translatable leaf (e.g. the step `name`) for language `lng`
// onto a node's `data`, returning a new data object. The default language edits
// the canonical top-level key; any other language edits `data.translations[lng]`
// so canonical values (and the status identifier) are never touched. Used by the
// canvas nodes whose label is edited inline instead of via StepDetailsForm.
export const setNodeLeafForLanguage = (data = {}, lng, key, value) => {
    if (!NODE_LEAF_KEYS.includes(key)) return data;
    if (!lng || lng === DEFAULT_LANGUAGE) {
        return { ...data, [key]: value };
    }
    return {
        ...data,
        translations: {
            ...(data?.translations || {}),
            [lng]: {
                ...(data?.translations?.[lng] || {}),
                [key]: value,
            },
        },
    };
};

// Build the values to load into the editor when translating into `lng`: every
// translatable text is replaced by its stored translation, or blanked so the
// author can fill it in. Shared (non-text) settings are kept untouched so the
// rest of the step config panel still renders normally.
export const buildNodeTranslationDraft = (data, lng) => {
    if (!data || lng === DEFAULT_LANGUAGE) return data;
    const overrides = data?.translations?.[lng] || {};
    const result = { ...data };
    for (const key of NODE_LEAF_KEYS) {
        if (typeof data[key] === "string") {
            const value = overrides[key];
            result[key] = typeof value === "string" ? value : "";
        }
    }
    return result;
};
